import { useState, useEffect, useRef } from "react";
import usePageLoader from '../hooks/usePageLoader';
import { ShimmerBox, ShimmerCircle, ShimmerFormField } from '../components/common/Shimmer';
import { useNavigate } from "react-router-dom";
import {
  FiUser, FiMail, FiLock, FiBell, FiSave, FiAlertTriangle,
  FiEye, FiEyeOff, FiEdit2, FiCheck, FiShield, FiGlobe,
  FiCpu, FiMessageCircle, FiCalendar, FiCreditCard, FiSettings,
  FiLogOut, FiCamera,
} from "react-icons/fi";
import toast from "react-hot-toast";

/* ── helpers ─────────────────────────────────────────── */
function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-1.5">{hint}</p>}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, disabled, type = "text", icon: Icon }) {
  return (
    <div className={`flex items-center gap-2.5 border rounded-xl px-3.5 py-2.5 transition ${
      disabled
        ? "bg-gray-50 border-gray-200"
        : "bg-white border-gray-200 focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary"
    }`}>
      {Icon && <Icon size={14} className="text-gray-400 shrink-0" />}
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        className="flex-1 text-sm outline-none bg-transparent text-gray-800 placeholder-gray-400 disabled:text-gray-500"
      />
    </div>
  );
}

function SectionCard({ title, subtitle, icon: Icon, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
      <div className="px-6 py-4 border-b bg-gray-50/70 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-primary-light flex items-center justify-center">
          <Icon size={14} className="text-primary" />
        </div>
        <div>
          <p className="text-sm font-bold text-gray-900">{title}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  );
}

function Toggle({ checked, onChange, label, desc }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b last:border-0">
      <div>
        <p className="text-sm font-medium text-gray-800">{label}</p>
        {desc && <p className="text-xs text-gray-400 mt-0.5">{desc}</p>}
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`relative shrink-0 w-10 h-5.5 h-[22px] rounded-full transition-colors mt-0.5 ${checked ? "bg-primary" : "bg-gray-200"}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-[18px] h-[18px] rounded-full bg-white shadow-sm transition-transform ${checked ? "translate-x-[18px]" : ""}`} />
      </button>
    </div>
  );
}

/* ── Main component ────────────────────────────────────── */
function UpdateProfileSkeleton() {
  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-5">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white border rounded-xl p-6 space-y-4">
                <div className="flex items-center gap-3 pb-4 border-b">
                  <ShimmerCircle size="w-20 h-20" />
                  <div className="space-y-2">
                    <ShimmerBox h="h-4" w="w-36" />
                    <ShimmerBox h="h-3" w="w-24" />
                    <ShimmerBox h="h-5" w="w-16" rounded="rounded-full" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[...Array(4)].map((_, j) => <ShimmerFormField key={j} />)}
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-5">
            <div className="bg-white border rounded-xl p-5 space-y-4">
              <ShimmerBox h="h-4" w="w-28" />
              <div className="grid grid-cols-2 gap-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-3 space-y-1">
                    <ShimmerBox h="h-5" w="w-12" />
                    <ShimmerBox h="h-3" w="w-16" />
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white border rounded-xl p-5 space-y-3">
              <ShimmerBox h="h-4" w="w-24" />
              {[...Array(4)].map((_, i) => <ShimmerBox key={i} h="h-9" w="w-full" rounded="rounded-lg" />)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function UpdateProfile() {
  const navigate  = useNavigate();
  const avatarRef = useRef(null);
  const loading = usePageLoader();

  const [user, setUser] = useState({ username: "", email: "" });

  /* form state */
  const [fullName,  setFullName]  = useState("");
  const [username,  setUsername]  = useState("");
  const [company,   setCompany]   = useState("");
  const [website,   setWebsite]   = useState("");
  const [bio,       setBio]       = useState("");
  const [timezone,  setTimezone]  = useState("Asia/Kolkata");
  const [language,  setLanguage]  = useState("English");

  /* password */
  const [currPwd,   setCurrPwd]   = useState("");
  const [newPwd,    setNewPwd]    = useState("");
  const [confPwd,   setConfPwd]   = useState("");
  const [showPwd,   setShowPwd]   = useState(false);

  /* notifications */
  const [notif, setNotif] = useState({
    leads:     true,
    training:  true,
    weekly:    false,
    monthly:   true,
    inbox:     true,
  });

  /* ui */
  const [savingProfile,  setSavingProfile]  = useState(false);
  const [savingPwd,      setSavingPwd]      = useState(false);
  const [deleteConfirm,  setDeleteConfirm]  = useState(false);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("user") || "{}");
    setUser(stored);
    setUsername(stored.username || "");
    setFullName(stored.fullName  || stored.username || "");
    setCompany(stored.company   || "");
    setWebsite(stored.website   || "");
    setBio(stored.bio         || "");
  }, []);

  const initials = (fullName || username || "U")
    .split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  const handleSaveProfile = async () => {
    if (!username.trim()) { toast.error("Username cannot be empty."); return; }
    setSavingProfile(true);
    await new Promise((r) => setTimeout(r, 700));
    const updated = { ...user, username, fullName, company, website, bio };
    localStorage.setItem("user", JSON.stringify(updated));
    setUser(updated);
    setSavingProfile(false);
    toast.success("Profile updated!");
  };

  const handleSavePwd = async () => {
    if (!currPwd) { toast.error("Enter your current password."); return; }
    if (newPwd.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (newPwd !== confPwd) { toast.error("Passwords do not match."); return; }
    setSavingPwd(true);
    await new Promise((r) => setTimeout(r, 700));
    setSavingPwd(false);
    setCurrPwd(""); setNewPwd(""); setConfPwd("");
    toast.success("Password changed successfully!");
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  return (
    <div className="-m-6 flex flex-col h-[calc(100vh-4rem)]">

      {/* ── Sticky header ── */}
      <div className="shrink-0 bg-white border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Profile & Account</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your personal info, security, and preferences.</p>
        </div>
        <button
          onClick={handleSaveProfile}
          disabled={savingProfile}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white text-sm font-semibold rounded-xl hover:opacity-90 transition disabled:opacity-70 shadow-sm shadow-primary/25"
        >
          {savingProfile
            ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : <FiSave size={14} />}
          {savingProfile ? "Saving…" : "Save Changes"}
        </button>
      </div>

      {loading ? <UpdateProfileSkeleton /> : <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* ── LEFT: main forms ── */}
            <div className="lg:col-span-2 space-y-5">

              {/* Avatar + identity */}
              <SectionCard title="Personal Information" subtitle="Your public profile details" icon={FiUser}>
                {/* Avatar row */}
                <div className="flex items-center gap-5 mb-6 pb-6 border-b">
                  <div className="relative">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-indigo-500 flex items-center justify-center text-white text-2xl font-bold shadow-md shadow-primary/25 select-none">
                      {initials}
                    </div>
                    <button
                      onClick={() => avatarRef.current?.click()}
                      className="absolute -bottom-1.5 -right-1.5 w-7 h-7 bg-white border-2 border-white rounded-full shadow flex items-center justify-center hover:bg-gray-50 transition"
                      title="Change avatar"
                    >
                      <FiCamera size={12} className="text-gray-600" />
                    </button>
                    <input ref={avatarRef} type="file" accept="image/*" className="hidden" />
                  </div>
                  <div>
                    <p className="text-base font-bold text-gray-900">{fullName || username || "Your Name"}</p>
                    <p className="text-sm text-gray-500">{user.email}</p>
                    <span className="inline-flex items-center gap-1 mt-1.5 text-[11px] font-bold bg-primary-light text-primary px-2 py-0.5 rounded-full">
                      <FiShield size={9} /> Pro Plan
                    </span>
                  </div>
                </div>

                {/* Form fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Full Name">
                    <TextInput
                      icon={FiUser}
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Your full name"
                    />
                  </Field>

                  <Field label="Username">
                    <TextInput
                      icon={FiEdit2}
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="username"
                    />
                  </Field>

                  <Field label="Email Address" hint="Contact support to change your email.">
                    <div className="flex items-center gap-2.5 border rounded-xl px-3.5 py-2.5 bg-gray-50 border-gray-200">
                      <FiMail size={14} className="text-gray-400 shrink-0" />
                      <span className="flex-1 text-sm text-gray-500">{user.email || "—"}</span>
                      <span className="flex items-center gap-1 text-[10px] font-bold bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">
                        <FiCheck size={9} /> Verified
                      </span>
                    </div>
                  </Field>

                  <Field label="Company / Organization">
                    <TextInput
                      icon={FiGlobe}
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      placeholder="Acme Inc."
                    />
                  </Field>

                  <Field label="Website" hint="Optional — your personal or company site.">
                    <TextInput
                      icon={FiGlobe}
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      placeholder="https://yourwebsite.com"
                    />
                  </Field>

                  <Field label="Timezone">
                    <div className="flex items-center gap-2.5 border rounded-xl px-3.5 py-2.5 bg-white border-gray-200 focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary transition">
                      <FiGlobe size={14} className="text-gray-400 shrink-0" />
                      <select
                        value={timezone}
                        onChange={(e) => setTimezone(e.target.value)}
                        className="flex-1 text-sm outline-none bg-transparent text-gray-800"
                      >
                        {[
                          "Asia/Kolkata", "America/New_York", "America/Los_Angeles",
                          "Europe/London", "Europe/Berlin", "Asia/Singapore",
                          "Asia/Tokyo", "Australia/Sydney",
                        ].map((tz) => (
                          <option key={tz} value={tz}>{tz.replace("_", " ")}</option>
                        ))}
                      </select>
                    </div>
                  </Field>
                </div>

                {/* Bio */}
                <div className="mt-4">
                  <Field label="Bio" hint="Brief description about you or your use case. Max 200 characters.">
                    <textarea
                      value={bio}
                      onChange={(e) => setBio(e.target.value.slice(0, 200))}
                      placeholder="Tell us about yourself or how you use NexaGPT…"
                      rows={3}
                      className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 placeholder-gray-400 outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition resize-none"
                    />
                    <p className="text-[11px] text-gray-400 text-right mt-1">{bio.length}/200</p>
                  </Field>
                </div>
              </SectionCard>

              {/* Language */}
              <SectionCard title="Language & Region" subtitle="Display preferences" icon={FiGlobe}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Display Language">
                    <div className="flex items-center gap-2.5 border rounded-xl px-3.5 py-2.5 bg-white border-gray-200 focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary transition">
                      <FiGlobe size={14} className="text-gray-400 shrink-0" />
                      <select
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        className="flex-1 text-sm outline-none bg-transparent text-gray-800"
                      >
                        {["English", "Hindi", "Spanish", "French", "German", "Arabic", "Japanese", "Portuguese"].map((l) => (
                          <option key={l}>{l}</option>
                        ))}
                      </select>
                    </div>
                  </Field>
                  <Field label="Date Format">
                    <div className="flex items-center gap-2.5 border rounded-xl px-3.5 py-2.5 bg-white border-gray-200 focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary transition">
                      <FiCalendar size={14} className="text-gray-400 shrink-0" />
                      <select className="flex-1 text-sm outline-none bg-transparent text-gray-800">
                        <option>MM / DD / YYYY</option>
                        <option>DD / MM / YYYY</option>
                        <option>YYYY-MM-DD</option>
                      </select>
                    </div>
                  </Field>
                </div>
              </SectionCard>

              {/* Password */}
              <SectionCard title="Password & Security" subtitle="Keep your account safe" icon={FiLock}>
                <div className="space-y-4">
                  <Field label="Current Password">
                    <div className="flex items-center gap-2.5 border rounded-xl px-3.5 py-2.5 bg-white border-gray-200 focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary transition">
                      <FiLock size={14} className="text-gray-400 shrink-0" />
                      <input
                        type={showPwd ? "text" : "password"}
                        value={currPwd}
                        onChange={(e) => setCurrPwd(e.target.value)}
                        placeholder="Current password"
                        className="flex-1 text-sm outline-none bg-transparent text-gray-800 placeholder-gray-400"
                      />
                    </div>
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="New Password" hint="At least 8 characters.">
                      <div className="flex items-center gap-2.5 border rounded-xl px-3.5 py-2.5 bg-white border-gray-200 focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary transition">
                        <FiLock size={14} className="text-gray-400 shrink-0" />
                        <input
                          type={showPwd ? "text" : "password"}
                          value={newPwd}
                          onChange={(e) => setNewPwd(e.target.value)}
                          placeholder="New password"
                          className="flex-1 text-sm outline-none bg-transparent text-gray-800 placeholder-gray-400"
                        />
                        <button onClick={() => setShowPwd((v) => !v)} className="text-gray-400 hover:text-gray-600 transition">
                          {showPwd ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                        </button>
                      </div>
                    </Field>

                    <Field label="Confirm New Password">
                      <div className={`flex items-center gap-2.5 border rounded-xl px-3.5 py-2.5 bg-white transition focus-within:ring-2 focus-within:ring-primary/30 ${
                        confPwd && confPwd !== newPwd ? "border-red-300 focus-within:ring-red-200" : "border-gray-200 focus-within:border-primary"
                      }`}>
                        <FiLock size={14} className="text-gray-400 shrink-0" />
                        <input
                          type={showPwd ? "text" : "password"}
                          value={confPwd}
                          onChange={(e) => setConfPwd(e.target.value)}
                          placeholder="Confirm password"
                          className="flex-1 text-sm outline-none bg-transparent text-gray-800 placeholder-gray-400"
                        />
                        {confPwd && confPwd === newPwd && (
                          <FiCheck size={14} className="text-green-500 shrink-0" />
                        )}
                      </div>
                    </Field>
                  </div>

                  {/* Password strength */}
                  {newPwd && (
                    <div>
                      <div className="flex gap-1 mb-1">
                        {[1, 2, 3, 4].map((i) => (
                          <div
                            key={i}
                            className={`flex-1 h-1 rounded-full transition-colors ${
                              newPwd.length >= i * 3
                                ? newPwd.length >= 12 ? "bg-green-500" : newPwd.length >= 8 ? "bg-yellow-400" : "bg-red-400"
                                : "bg-gray-100"
                            }`}
                          />
                        ))}
                      </div>
                      <p className="text-[11px] text-gray-400">
                        {newPwd.length < 6 ? "Weak" : newPwd.length < 8 ? "Fair" : newPwd.length < 12 ? "Good" : "Strong"}
                      </p>
                    </div>
                  )}

                  <button
                    onClick={handleSavePwd}
                    disabled={savingPwd}
                    className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white text-sm font-semibold rounded-xl hover:opacity-90 transition disabled:opacity-70 shadow-sm shadow-primary/25"
                  >
                    {savingPwd
                      ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <FiLock size={13} />}
                    {savingPwd ? "Updating…" : "Update Password"}
                  </button>
                </div>
              </SectionCard>

              {/* Notifications */}
              <SectionCard title="Notification Preferences" subtitle="Choose what emails you receive" icon={FiBell}>
                <div className="divide-y divide-gray-50">
                  <Toggle
                    checked={notif.leads}
                    onChange={(v) => setNotif({ ...notif, leads: v })}
                    label="Lead captured"
                    desc="Get notified when a new lead submits their info in the chat."
                  />
                  <Toggle
                    checked={notif.training}
                    onChange={(v) => setNotif({ ...notif, training: v })}
                    label="Training completed"
                    desc="Alert when a chatbot finishes indexing new content."
                  />
                  <Toggle
                    checked={notif.inbox}
                    onChange={(v) => setNotif({ ...notif, inbox: v })}
                    label="New inbox conversation"
                    desc="Email when a visitor starts a live conversation."
                  />
                  <Toggle
                    checked={notif.weekly}
                    onChange={(v) => setNotif({ ...notif, weekly: v })}
                    label="Weekly usage digest"
                    desc="Summary of messages, leads, and conversations every Monday."
                  />
                  <Toggle
                    checked={notif.monthly}
                    onChange={(v) => setNotif({ ...notif, monthly: v })}
                    label="Monthly analytics report"
                    desc="Your monthly performance summary with trends and insights."
                  />
                </div>
              </SectionCard>

              {/* Danger zone */}
              <div className="bg-white border border-red-100 rounded-2xl overflow-hidden shadow-sm">
                <div className="px-6 py-4 border-b border-red-100 bg-red-50/50 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                    <FiAlertTriangle size={14} className="text-red-500" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">Danger Zone</p>
                    <p className="text-xs text-gray-400 mt-0.5">Irreversible actions — proceed with caution</p>
                  </div>
                </div>
                <div className="px-6 py-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Delete account</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Permanently delete your account, all chatbots, and data. This cannot be undone.
                      </p>
                    </div>
                    <button
                      onClick={() => setDeleteConfirm(true)}
                      className="shrink-0 px-4 py-2 text-xs font-semibold text-red-600 border border-red-200 rounded-xl hover:bg-red-50 transition"
                    >
                      Delete Account
                    </button>
                  </div>
                </div>
              </div>

            </div>

            {/* ── RIGHT: stats + quick actions ── */}
            <div className="space-y-5">

              {/* Account stats */}
              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                <div className="bg-gradient-to-br from-primary to-indigo-500 px-5 pt-5 pb-8">
                  <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white text-xl font-bold shadow-sm ring-2 ring-white/20 mb-3">
                    {initials}
                  </div>
                  <p className="text-white font-bold text-base leading-tight">{fullName || username || "Your Name"}</p>
                  <p className="text-white/70 text-xs mt-0.5">{user.email}</p>
                </div>

                <div className="px-5 -mt-5 pb-5 relative z-10">
                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3 mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-bold bg-primary-light text-primary px-1.5 py-0.5 rounded-full">Pro Plan</span>
                      <span className="text-xs text-gray-400">Active</span>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">Renews Jun 10, 2026</p>
                  </div>

                  <div className="grid grid-cols-2 gap-2.5">
                    <StatTile icon={FiCpu}            label="Chatbots"   value="3"   color="text-violet-500" bg="bg-violet-50" />
                    <StatTile icon={FiMessageCircle}  label="Msgs/mo"    value="8.4k" color="text-blue-500"   bg="bg-blue-50" />
                    <StatTile icon={FiCalendar}       label="Member for" value="5 mo" color="text-emerald-500" bg="bg-emerald-50" />
                    <StatTile icon={FiShield}         label="2FA"        value="Off"  color="text-orange-500" bg="bg-orange-50" />
                  </div>
                </div>
              </div>

              {/* Quick actions */}
              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                <div className="px-5 py-3.5 border-b bg-gray-50/70">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Quick Actions</p>
                </div>
                <div className="divide-y divide-gray-50">
                  {[
                    { icon: FiCreditCard, label: "Manage Billing",   sub: "Plans & invoices",     action: () => navigate("/billing") },
                    { icon: FiSettings,   label: "Chatbot Settings",  sub: "Configure your bots",  action: () => navigate("/advanced/settings") },
                    { icon: FiCpu,        label: "My Chatbots",       sub: "View all chatbots",    action: () => navigate("/chatbots") },
                    { icon: FiLogOut,     label: "Log Out",           sub: "Sign out of account",  action: handleLogout, danger: true },
                  ].map(({ icon: Icon, label, sub, action, danger }) => (
                    <button
                      key={label}
                      onClick={action}
                      className={`w-full flex items-center gap-3 px-5 py-3 text-left transition ${
                        danger ? "hover:bg-red-50" : "hover:bg-gray-50"
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        danger ? "bg-red-50" : "bg-gray-100"
                      }`}>
                        <Icon size={13} className={danger ? "text-red-500" : "text-gray-500"} />
                      </div>
                      <div>
                        <p className={`text-sm font-medium leading-tight ${danger ? "text-red-600" : "text-gray-800"}`}>{label}</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>}

      {/* ── Delete confirm modal ── */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center mb-4">
              <FiAlertTriangle size={20} className="text-red-500" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-1">Delete your account?</h3>
            <p className="text-sm text-gray-500 mb-5">
              This will permanently delete your account, all chatbots, conversations, and data.
              <span className="font-semibold text-gray-700"> This action cannot be undone.</span>
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(false)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => { toast.error("Account deletion requires email confirmation."); setDeleteConfirm(false); }}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 transition"
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function StatTile({ icon: Icon, label, value, color, bg }) {
  return (
    <div className={`${bg} rounded-xl px-3 py-2.5 flex flex-col gap-1`}>
      <Icon size={12} className={color} />
      <p className="text-sm font-bold text-gray-900 leading-none">{value}</p>
      <p className="text-[10px] text-gray-400 font-medium">{label}</p>
    </div>
  );
}
